import { useEffect, useState, type FormEvent } from "react";
import {
  createTenant,
  formatApiError,
  getMe,
  getTenants,
  type Me,
  type Tenant,
} from "@/api";
import { useAuth } from "@/auth";
import { Link } from "react-router-dom";

export function Tenants() {
  const { status } = useAuth();
  const [me, setMe] = useState<Me | null>(null);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [newKey, setNewKey] = useState("");
  const [newName, setNewName] = useState("");

  // The api client reads its own creds (Basic from sessionStorage, or the
  // Google cookie via `credentials: "include"`), so we never thread them.
  // Re-runs on auth `status` change, never gated on creds truthiness.
  const reload = () => {
    getTenants()
      .then((r) => setTenants(r.tenants))
      .catch(() => setError("Could not load tenants."));
  };

  useEffect(() => {
    getMe().then(setMe).catch(() => setError("Could not load profile."));
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const onCreate = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await createTenant({ key: newKey, name: newName });
      setNewKey("");
      setNewName("");
      reload();
    } catch (err) {
      setError(formatApiError(err, "Could not create tenant."));
    }
  };

  const isOperator = me?.role === "operator";

  return (
    <div className="space-y-8">
      <div className="rise">
        <h1 className="font-display text-3xl font-bold uppercase tracking-tight">Clients</h1>
        <p className="mt-1 text-sm text-dim">Your clients on the platform.</p>
      </div>

      {error && <p role="alert" className="text-coral">{error}</p>}

      <ul className="rise card divide-y divide-line overflow-hidden">
        {tenants.map((t) => (
          <li key={t.key}>
            <Link
              to={`/tenants/${t.key}`}
              className="flex items-center gap-3 px-5 py-3 transition-colors hover:bg-ink-1"
            >
              <span className="tag">{t.key}</span>
              <span className="text-text">{t.name}</span>
              <span aria-hidden="true" className="ml-auto text-dim">→</span>
            </Link>
          </li>
        ))}
      </ul>

      {isOperator && (
        <form onSubmit={onCreate} className="card space-y-4 p-5">
          <h2 className="label">New client</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="new-key" className="label">New client key</label>
              <input
                id="new-key"
                className="field"
                value={newKey}
                onChange={(e) => setNewKey(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="new-name" className="label">New client name</label>
              <input
                id="new-name"
                className="field"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
              />
            </div>
          </div>
          <button type="submit" className="btn-primary">Create client</button>
        </form>
      )}
    </div>
  );
}
